import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';

import { saveReturnPath } from './return-path';
import { useAuth } from './useAuth';

const PUBLIC_PATHS = new Set<string>(['/login', '/auth/callback']);

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPublic = PUBLIC_PATHS.has(location.pathname);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isPublic) {
      // Stash the intended deep link so the post-login flow lands the user back
      // here instead of the generic dashboard. NOTE: TanStack's `location.href`
      // is ORIGIN-RELATIVE (pathname + search + hash, e.g.
      // `/lva/LVA-2026-00001?c=<companyId>`), NOT a full URL — so it is already a
      // same-origin path that passes isSafeReturnPath. (Do not "fix" this to
      // pathname + search: `location.search` is a parsed OBJECT here.)
      saveReturnPath(location.href);
      void navigate({ to: '/login' });
    }
  }, [isAuthenticated, isLoading, isPublic, location.href, navigate]);

  if (isPublic) return <>{children}</>;
  if (isLoading) return <FullPageLoader />;
  if (!isAuthenticated) return null;
  return <>{children}</>;
}

export function FullPageLoader() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg, #0e1220)',
        color: 'var(--color-text, #e4e7ef)',
        gap: 16,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          border: '3px solid rgba(255,255,255,0.12)',
          borderTopColor: 'var(--brand, #C00000)',
          animation: 'dcc-spin 0.9s linear infinite',
        }}
      />
      <div className="muted" style={{ fontSize: 13 }}>
        กำลังโหลด…
      </div>
      <style>{`@keyframes dcc-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
